/**
 * Redux Store Configuration for Relay Protocol Commerce UI
 */

import { configureStore } from '@reduxjs/toolkit';
import { useDispatch, useSelector, TypedUseSelectorHook } from 'react-redux';

// Import slices
import authSlice from './slices/authSlice';
import marketplaceSlice from './slices/marketplaceSlice';
import commerceSlice from './slices/commerceSlice';
import dashboardSlice from './slices/dashboardSlice';
import uiSlice from './slices/uiSlice';
import executionSlice from './slices/executionSlice';

const isProduction =
  typeof import.meta !== 'undefined' &&
  typeof (import.meta as Record<string, any>).env !== 'undefined' &&
  (import.meta as Record<string, any>).env.NODE_ENV === 'production';

// Configure store with all slices
export const store = configureStore({
  reducer: {
    auth: authSlice,
    marketplace: marketplaceSlice,
    commerce: commerceSlice,
    dashboard: dashboardSlice,
    execution: executionSlice,
    ui: uiSlice
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore specific action types that might contain non-serializable values
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
        ignoredActionsPaths: ['meta.arg', 'payload.timestamp'],
        ignoredPaths: ['items.dates']
      }
    }),
  devTools: !isProduction
});

// Export types for TypeScript support
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Export typed hooks for components
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export default store;
